import { useEffect, useMemo, useState } from 'react';

export type RaffleCountdownTarget = Date | number | string;

type RaffleCountdownParts = {
  days: number;
  hours: number;
  isExpired: boolean;
  minutes: number;
  remainingMs: number;
  remainingSeconds: number;
  seconds: number;
};

type RaffleCountdownSnapshot = RaffleCountdownParts & {
  countdownText: string;
};

const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * 60;
const SECONDS_PER_DAY = SECONDS_PER_HOUR * 24;

function resolveTargetMs(targetAt: RaffleCountdownTarget): number | null {
  const resolvedAt =
    targetAt instanceof Date
      ? targetAt.getTime()
      : new Date(targetAt).getTime();

  return Number.isFinite(resolvedAt) ? resolvedAt : null;
}

function formatCountdownPart(value: number): string {
  return String(Math.max(0, value)).padStart(2, '0');
}

function resolveCountdownParts(remainingSeconds: number): RaffleCountdownParts {
  const totalSeconds = Math.max(0, remainingSeconds);
  const days = Math.floor(totalSeconds / SECONDS_PER_DAY);
  const hours = Math.floor((totalSeconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
  const minutes = Math.floor(
    (totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE,
  );
  const seconds = totalSeconds % SECONDS_PER_MINUTE;

  return {
    days,
    hours,
    isExpired: totalSeconds === 0,
    minutes,
    remainingMs: totalSeconds * MILLISECONDS_PER_SECOND,
    remainingSeconds: totalSeconds,
    seconds,
  };
}

function formatCountdownText(parts: RaffleCountdownParts): string {
  return [
    formatCountdownPart(parts.days),
    formatCountdownPart(parts.hours),
    formatCountdownPart(parts.minutes),
    formatCountdownPart(parts.seconds),
  ].join(' : ');
}

export function useRaffleCountdown(
  targetAt: RaffleCountdownTarget,
): RaffleCountdownSnapshot {
  const targetMs = useMemo(() => resolveTargetMs(targetAt), [targetAt]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (targetMs === null || targetMs <= nowMs) {
      return;
    }

    const timerId = window.setTimeout(() => {
      setNowMs(Date.now());
    }, MILLISECONDS_PER_SECOND);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [nowMs, targetMs]);

  const remainingSeconds = useMemo(() => {
    if (targetMs === null) {
      return 0;
    }

    return Math.max(0, Math.ceil((targetMs - nowMs) / MILLISECONDS_PER_SECOND));
  }, [nowMs, targetMs]);

  const parts = useMemo(
    () => resolveCountdownParts(remainingSeconds),
    [remainingSeconds],
  );

  return {
    ...parts,
    countdownText: formatCountdownText(parts),
  };
}
